<script setup lang="ts">
import { watch, ref, provide, inject } from 'vue';
import { useRoute } from 'vue-router';
import ProjectConfig from '@/views/fragments/ProjectConfig.vue';
import PartsConfig from '@/views/fragments/PartsConfig.vue';
import PerspectiveConfig from '@/views/fragments/PerspectiveConfig.vue';
import HotspotConfig from '@/views/fragments/HotspotConfig.vue';
import LightingConfig from '@/views/fragments/LightingConfig.vue';
import DragAndDrop from '@/components/DragAndDrop.vue';
import SideMenu from '@/components/SideMenu.vue';
import ViewerCanvas from '@/components/Viewer/ViewerCanvas.vue';
import CustomConfig from '@/views/fragments/CustomConfig.vue';
import PreviewProject from '@/components/PreviewProject.vue';

import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { Component, Ref } from 'vue';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';
import type { Hotspots, ManifestHandler } from '@/viewerExtensions';

const route: RouteLocationNormalizedLoaded = useRoute();
const activeRoute = ref(route.path);
const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;

const hotspotsExtension = ref(viewer.value?.extensions.get('Hotspots') as Nullable<Extension<Hotspots>>);
const manifestHandlerExtension = ref(viewer.value?.extensions.get('ManifestHandler') as Nullable<Extension<ManifestHandler>>);

const configColumMapping = new Map<string, Component>([
  ['/', ProjectConfig],
  ['/parts', PartsConfig],
  ['/lighting', LightingConfig],
  ['/perspectives', PerspectiveConfig],
  ['/hotspots', HotspotConfig],
  ['/custom', CustomConfig],
]);

const createHotspots = () => {
  const hotspotNames = Object.keys(manifest.value.hotspots);

  hotspotsExtension.value?.destroyAllHotspots();

  if (viewer.value) {
    viewer.value.renderNextFrame += 1;
  }

  for (const hotspotName of hotspotNames) {
    const hotspotInfo = manifest.value.hotspots[hotspotName];

    hotspotsExtension.value?.createHotspot(hotspotInfo);

    if (hotspotInfo.startVisible === true) {
      hotspotsExtension.value?.showHotspot(hotspotName);
    }
  }

  if (viewer.value) {
    viewer.value.renderNextFrame += 1;
  }
};

const routePathWatcher = (route: string) => {
  activeRoute.value = route;

  createHotspots();
};

const manifestWatcher = (newManifest: IManifest, oldManifest: IManifest) => {
  if (JSON.stringify(newManifest) === JSON.stringify(oldManifest)) return;

  manifestHandlerExtension.value?.loadManifest(newManifest);

  createHotspots();
};

const viewerWatcher = () => {
  if (!viewer.value) return;

  manifestHandlerExtension.value = viewer.value.extensions.get('ManifestHandler') as Extension<ManifestHandler>;
  hotspotsExtension.value = viewer.value.extensions.get('Hotspots') as Extension<Hotspots>;

  manifestHandlerExtension.value.loadManifest(manifest.value);

  window.manifest = manifest.value;
};

watch(() => route.path, routePathWatcher);
watch(manifest, manifestWatcher);
watch(viewer, viewerWatcher);

provide('manifest', manifest);
</script>

<template>
  <PreviewProject v-if="activeRoute === '/preview'" class="absolute z-50 !w-screen top-[37px] flex flex-row" style="height: calc(100vh - 37px)" />
  <div class="flex flex-row max-w-screen h-full" style="max-height: calc(100vh - 37px)">
    <SideMenu v-once />
    <ViewerCanvas v-once />
    <div v-if="viewer" class="flex flex-col w-[300px] min-w-[300px] border-l px-1 bg-white z-10">
      <div id="config-column" class="flex flex-col max-h-full overflow-y-auto relative">
        <component :is="configColumMapping.get(activeRoute)" v-if="configColumMapping.has(activeRoute)" />
      </div>
      <div class="pt-1 mt-auto">
        <DragAndDrop v-once />
      </div>
    </div>
  </div>
</template>
